import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { CircleCheck, X } from 'lucide-react';
import { useFocusToast } from '../../UnitFocus/useFocusToast';
import { ExitSlipToastMessage } from '../../../businessLogicService/exitSlipSnapshotHelper';

const MOVE_TOAST_LIFE_MS = 8000;

export interface ExitSlipMoveToastHandle {
  show: (message: ExitSlipToastMessage) => void;
  clear: () => void;
}

const ExitSlipSnapshotMoveToast = forwardRef<ExitSlipMoveToastHandle>(function ExitSlipSnapshotMoveToast(_props, ref) {
  const toastRef = useRef<Toast>(null);
  const showToast = useFocusToast(toastRef, MOVE_TOAST_LIFE_MS);

  useImperativeHandle(
    ref,
    () => ({
      show: ({ header, detail }) => showToast(header, detail),
      clear: () => toastRef.current?.clear(),
    }),
    [showToast],
  );

  return (
    <Toast
      ref={toastRef}
      position="bottom-center"
      appendTo={document.body}
      className="tw-opacity-100"
      pt={{
        root: { className: '!tw-w-[580px] !tw-max-w-[90vw]' },
        message: { className: '!tw-bg-transparent !tw-border-none !tw-shadow-none' },
        content: { className: '!tw-p-0' },
      }}
      content={({ message }) => (
        <div className="tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-2xl tw-border-2 tw-border-b-8 tw-border-solid tw-border-green-800 tw-bg-green-700 tw-p-4 tw-font-atkinson tw-text-neutral-0">
          <CircleCheck size={24} className="tw-shrink-0" aria-hidden />
          <div className="tw-flex tw-min-w-0 tw-grow tw-flex-col tw-gap-2">
            <span className="tw-text-base tw-font-bold tw-leading-5 [text-wrap:pretty]">{message.summary}</span>
            {!!message.detail && <span className="tw-text-sm tw-leading-5 [text-wrap:pretty]">{message.detail}</span>}
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => toastRef.current?.remove(message)}
            className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-p-0 tw-text-neutral-0"
          >
            <X size={16} strokeWidth={2.5} aria-hidden />
          </button>
        </div>
      )}
    />
  );
});

export default ExitSlipSnapshotMoveToast;
